import type { MinMaxNumberSettings } from "../types/min-max-number-settings"
import { observer } from "mobx-react-lite"

import { cn } from "@/domains/library/cn"

interface MinMaxNumberFilterProps {
  /** The current filter settings */
  settings: MinMaxNumberSettings
  /** Callback when the numeric value changes */
  onValueChange: (value: string) => void
  /** Callback when the min/max mode changes */
  onModeChange: (isMin: boolean) => void
  /** Label for the filter (e.g., "TVL", "APR") */
  label: string
  id: string
  name: string
  className?: string
}

/**
 * Reusable component for min/max number filtering
 * Handles numeric input validation and min/max mode toggling
 */
export const MinMaxNumberFilter = observer(
  ({ settings, onValueChange, onModeChange, label, id, name, className }: MinMaxNumberFilterProps) => {
    // Handle input changes with validation
    const handleInputChange = (value: string) => {
      // Only allow numbers and one decimal point
      const numericRegex = /^[0-9]*\.?[0-9]*$/
      if (value === "" || numericRegex.test(value)) {
        onValueChange(value)
      }
    }

    // Toggle between min and max modes
    const handleModeToggle = () => {
      onModeChange(!settings.isMin)
    }

    return (
      <div
        className={cn(
          "flex max-w-[300px] items-center rounded-md border border-dropdown-border bg-dropdown focus-within:border-blue-500",
          className,
        )}
      >
        {/* Label prefix - fixed width */}
        <span className="py-2 pl-3 pr-0 text-sm font-medium">{label}</span>

        {/* Numeric input - flexible width with max constraint */}
        <input
          id={id}
          name={name}
          type="text"
          value={settings.value}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder="0"
          className="max-w-[120px] flex-1 border-0 bg-transparent px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
        />

        {/* Mode toggle button - fixed width */}
        <button
          type="button"
          onClick={handleModeToggle}
          className="w-12 cursor-pointer rounded-r px-2 py-2 text-xs font-medium bg-dropdown-highlight-25 hover:bg-dropdown-highlight hover:text-dropdown-highlight-content"
        >
          {settings.isMin ? "min" : "max"}
        </button>

        <span className="w-[1px]"></span>
      </div>
    )
  },
)
